/**
 * Navigation item capability requirements.
 *
 * Maps each navigation item to the capabilities required to access it.
 * Used for capability-based sidebar visibility filtering.
 *
 * Source of truth: backend responsibility definitions + route guards.
 * Items without a required capability are visible to their role by default.
 */

export const NAVIGATION_CAPABILITY_MAP = {
  // Dashboard & Core
  'dashboard': null, // Accessible to all authenticated users
  'dashboard-faculty': null, // role-based
  'admin-dashboard': null, // admin-only
  'home-student': null, // student-only

  // Teaching (Faculty)
  'faculty-diary': 'MANAGE_OWN_DIARY',
  'lesson-plans': 'MANAGE_OWN_LESSON_PLAN',
  'cw-assignments': 'VIEW_ASSIGNMENTS',
  'faculty-assessments': 'MANAGE_ASSESSMENTS',

  // Workspace
  'cw-overview': 'VIEW_BATCH_DASHBOARD',
  'cw-batches': ['VIEW_BATCHES', 'MANAGE_BATCHES'], // ANY of these
  'cw-students': 'MANAGE_STUDENTS',
  'cw-interventions': 'TRIAGE_MONITORING_CASES',
  'cw-activity-feed': ['MANAGE_STUDENTS', 'MANAGE_BATCHES'], // CBC faculty access

  // Problem Intelligence
  'problem-library': 'AUTHOR_PROBLEM',
  'problem-library-analytics': 'CURATE_PROBLEM',

  // Analytics
  'an-students': 'GRADE_SUBMISSIONS',
  'an-batches': 'GRADE_SUBMISSIONS',
  'an-overall': 'VIEW_BATCH_DASHBOARD',
  'an-effectiveness': 'VIEW_TEACHING_REPORTS',
  'an-platforms': 'VIEW_BATCH_DASHBOARD', // Platform analytics is cbcfaculty-only but needs this capability
  'teaching-reports': 'VIEW_TEACHING_REPORTS',
  'reporting-intelligence': 'VIEW_TEACHING_REPORTS',

  // Administration
  'courses': 'AUTHOR_PROBLEM', // Question Bank shared with problem authoring
  'questions': 'AUTHOR_PROBLEM',
  'labs': null, // Need to verify - Lab Internals may not have a capability requirement

  // Reviews
  'reviews-labs': null, // Need to verify
  'reviews-projects': null, // Need to verify

  // Students Section
  'cw-students-section': 'MANAGE_STUDENTS', // VIEW_STUDENTS might also apply
  'cw-interventions-section': 'TRIAGE_MONITORING_CASES',

  // Profile
  'profile-link': 'EDIT_OWN_PROFILE',

  // Placement
  'placement': null, // Dynamic based on coordinator role

  // Admin sections
  'org-batches': null, // admin-only
  'org-users': null, // admin-only
  'ac-analytics': null, // admin-only
  'ac-diary-transfer': null, // admin-only
  'ac-courses': null, // admin-only
  'ac-labs': null, // admin-only
  'ci-platform': null, // admin-only
  'ci-problem-library': null, // admin-only
  'ci-problem-library-analytics': null, // admin-only

  // Student sections
  'coding-dashboard': null, // student-only, all students
  'coding-progress': null, // student-only
  'my-courses': null, // student-only
  'student-assignments': null, // student-only
  'student-assessments': null, // student-only
};

/**
 * Get the required capability(ies) for a navigation item.
 *
 * @param {string} itemId - Navigation item ID
 * @returns {string | string[] | null} - Single capability, array of capabilities (ANY), or null (no requirement)
 */
export function getRequiredCapability(itemId) {
  return NAVIGATION_CAPABILITY_MAP[itemId] ?? null;
}

/**
 * Check if an item has capability requirements.
 *
 * @param {string} itemId - Navigation item ID
 * @returns {boolean}
 */
export function hasCapabilityRequirement(itemId) {
  return getRequiredCapability(itemId) !== null;
}
